import { useMutation } from '@ngtrvu/use-api';
import { notification } from 'antd';
import { Button } from 'common/ui/button';
import { PasswordInput } from 'common/ui/password-input';
import { useAdminAccount } from 'hooks/use-admin-account';
import { useState } from 'react';
import { setAccessToken } from 'utils/auth';
import { STAFF_PASSWORD_HINT, staffPasswordError } from 'utils/password-policy';
import changeAccountPasswordApi from '../../../api-calls/change-password';

export default function ChangePassword() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const { data: account } = useAdminAccount();

  // @ts-ignore — local ApiCall shape differs from @ngtrvu/use-api's ApiCall type
  const { mutate, isPending } = useMutation(changeAccountPasswordApi, {
    onSuccess: (response: any) => {
      // Changing the password signs out every session that used a password
      // login, this one included; the server hands back a fresh token for it.
      const accessToken = response?.data?.access_token;
      if (accessToken) {
        setAccessToken(accessToken);
      }
      notification.open({
        type: 'success',
        message: 'Đổi mật khẩu thành công',
        description: 'Các phiên đăng nhập khác bằng mật khẩu đã bị đăng xuất.',
      });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    },
    // use-api passes the thrown error; lib/api-call puts the body on .response
    onError: (err: any) => {
      const { error, error_code } = err?.response || {};
      notification.open({
        type: 'error',
        message:
          error_code === 'incorrect_pwd'
            ? 'Mật khẩu hiện tại không đúng.'
            : `Lỗi: ${error || error_code || err?.message}`,
      });
    },
  });

  const validate = () => {
    if (!oldPassword) {
      return 'Vui lòng nhập mật khẩu hiện tại.';
    }
    if (!newPassword.trim()) {
      return 'Vui lòng nhập mật khẩu mới.';
    }
    const policyError = staffPasswordError(newPassword, account?.email);
    if (policyError) {
      return policyError;
    }
    if (newPassword !== confirmPassword) {
      return 'Mật khẩu nhập lại không khớp.';
    }
    if (newPassword === oldPassword) {
      return 'Mật khẩu mới phải khác mật khẩu hiện tại.';
    }
    return '';
  };

  const handleSubmit = e => {
    e.preventDefault();
    const validationError = validate();
    setError(validationError);
    if (validationError) return;
    mutate({ old_password: oldPassword, new_password: newPassword });
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        <h2 className="mb-6 text-2xl font-bold text-center">Đổi mật khẩu</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block mb-1 font-medium">Mật khẩu hiện tại</label>
            <PasswordInput
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              placeholder="Nhập mật khẩu hiện tại"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Mật khẩu mới</label>
            <PasswordInput
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Nhập mật khẩu mới"
              autoComplete="new-password"
              required
            />
            <p className="mt-1 text-xs text-gray-500">{STAFF_PASSWORD_HINT}</p>
          </div>
          <div>
            <label className="block mb-1 font-medium">Nhập lại mật khẩu mới</label>
            <PasswordInput
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Nhập lại mật khẩu mới"
              autoComplete="new-password"
              required
            />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}

          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? 'Đang đổi...' : 'Đổi mật khẩu'}
          </Button>
        </form>
      </div>
    </div>
  );
}
